import React, { useCallback, useEffect, useRef, useState } from 'react';
import { mapApi } from '../../../requests/api';
import { CopyIcon, ExternalIcon, RefreshIcon, TickIcon } from '../icons';
import { MatchEvidence } from './LeadMatchesModal';
import { ComptrollerLink } from './OwnerChoice';

/**
 * Who this building's investor is — one question, three sources.
 *
 * The people LightBox names behind the assessor's entity, the entity itself,
 * and an investor iCLIMB already holds. They were three separate controls
 * under one heading and picking in one silently cleared the others, which read
 * as three competing questions.
 *
 * Each source is its own card under its own heading, and one radio runs across
 * all of them. Picking a row opens it and closes whatever was open before —
 * only one owner can be chosen, so only one row is ever expanded, and an
 * opened row shows everything at once. Nothing inside it collapses again: a
 * second click to see a phone number is a second click too many.
 */

const SEARCH_DEBOUNCE_MS = 350;
const SEARCH_MIN_CHARS = 2;

export const RECORD = 'record';

// The server's word for a record on file that is the LightBox person themselves; anything else only shares a detail.
const SAME_PERSON = 'definite';

// Every record iCLIMB holds that may be this LightBox person, likeliest first.
function onFileOf(row) {
    return row.on_file || [];
}

// The record is them, or carries their name or a nickname of it — not another person sharing an email.
function isThem(record) {
    return record.strength === SAME_PERSON
        || (record.matched || []).some((each) => each.field === 'name' || each.field === 'nickname');
}

/** The vendor marks verified addresses "(v)"; that is a flag, not the address. */
function cleanEmail(value) {
    return String(value || '').replace(/\(v\)$/i, '').trim();
}

function addressLine(address) {
    if (!address) return null;

    const tail = [address.city, address.state, address.zip].filter(Boolean).join(' ');

    return [address.street, tail].filter(Boolean).join(', ') || null;
}

function rowName(row) {
    return row.name || (row.business && row.business.name) || 'Unnamed contact';
}

/**
 * How well the provider thinks this person matches, as a strength.
 *
 * LightBox scores 1 best and 5 worst, which every reader gets backwards. Five
 * bars filled from the left need no caption: more is better, the way a signal
 * or a password strength reads.
 */
function MatchMeter({ confidence }) {
    if (confidence === null || confidence === undefined) return null;

    const strength = Math.max(1, Math.min(5, 6 - confidence));
    const label = 'Match strength ' + strength + ' of 5';

    return (
        <span className="OwnerMeter" title={label} aria-label={label}>
            {[1, 2, 3, 4, 5].map((step) => (
                <span key={step} className={'OwnerMeter-bar' + (step <= strength ? ' OwnerMeter-bar--on' : '')} />
            ))}
        </span>
    );
}

/**
 * One candidate, whichever source it came from.
 *
 * The name gets a line of its own and the badges get theirs. Sharing one line
 * they wrapped into the name and the row read as a jumble.
 *
 * The body grows from nothing rather than arriving at full height. It has to be
 * mounted closed for one frame first, or there is no start value to animate
 * from — which is why the open state is held here instead of read off the prop.
 */
function Row({ name, sub, pills, meter, isOn, isOpen, onPick, children }) {
    const shows = Boolean(isOn && isOpen && children);
    const [grown, setGrown] = useState(false);

    useEffect(() => {
        if (!shows) {
            setGrown(false);

            return undefined;
        }

        const frame = window.requestAnimationFrame(() => setGrown(true));

        return () => window.cancelAnimationFrame(frame);
    }, [shows]);

    return (
        <div className={'OwnerRow' + (isOn ? ' OwnerRow--on' : '')}>
            <button type="button" className="OwnerRow-pick" onClick={onPick}>
                <span className="OwnerRow-radio" />
                <span className="OwnerRow-body">
                    <span className="OwnerRow-name">{name}</span>
                    {pills && <span className="OwnerRow-pills">{pills}</span>}
                    {sub && <span className="OwnerRow-sub">{sub}</span>}
                </span>
                {meter}
            </button>
            {shows && (
                <div className={'OwnerRow-open' + (grown ? ' OwnerRow-open--grown' : '')}>
                    <div className="OwnerRow-openInner">{children}</div>
                </div>
            )}
        </div>
    );
}

/** Clipboard without the API: a textarea off-screen, selected and cut. */
function fallbackCopy(text) {
    const field = document.createElement('textarea');

    field.value = text;
    field.setAttribute('readonly', '');
    field.style.position = 'absolute';
    field.style.left = '-9999px';
    document.body.appendChild(field);
    field.select();

    const ok = document.execCommand('copy');

    document.body.removeChild(field);

    return ok;
}

function Group({ label, action, children }) {
    return (
        <div className="OwnerGroup">
            <div className="OwnerGroup-head">
                <span className="OwnerGroup-label">{label}</span>
                <span className="OwnerGroup-spacer" />
                {action}
            </div>
            <div className="OwnerGroup-card">{children}</div>
        </div>
    );
}

/**
 * Which of a contact's numbers becomes the one on the investor row.
 *
 * An INVESTORS row holds one landline, one mobile and one address where
 * LightBox often sends five of each, so showing them and choosing between
 * them is the same control — the numbers are the thing a broker dials.
 */
function ContactChoice({ label, options, value, onChange, markOf, same }) {
    if (!options.length) return null;

    // One number is not a choice, and a radio that cannot be moved reads as one.
    if (options.length === 1) {
        const mark = markOf && markOf(options[0]);

        return (
            <div className="OwnerOpen-block">
                <Fact label={label} value={options[0] + (mark ? ' · ' + mark : '')} />
            </div>
        );
    }

    const isChosen = same || ((option, chosen) => option === chosen);

    return (
        <div className="OwnerOpen-block">
            <div className="OwnerOpen-label">{label}</div>
            {options.map((option) => (
                <button
                    type="button"
                    key={option}
                    className={'OwnerOpen-choice' + (isChosen(option, value) ? ' OwnerOpen-choice--on' : '')}
                    onClick={() => onChange(option)}
                >
                    <span className="OwnerOpen-dot" />
                    <span className="OwnerOpen-value">{option}</span>
                    {markOf && markOf(option) && <span className="OwnerOpen-mark">{markOf(option)}</span>}
                </button>
            ))}
        </div>
    );
}

/**
 * What happens to whoever holds the property now.
 *
 * A segmented control rather than two more radios: the opened row already holds
 * three radio groups and a fourth vanished among them — and this is the one
 * choice on the card that can archive a real owner, so it cannot be missed.
 *
 * The label names them, because "Replace Christopher Tessitore" says nothing
 * when the property has three.
 */
function OwnershipChoice({ owners, alongside, onChange }) {
    if (!owners.length) return null;

    const replace = owners.length === 1
        ? 'Replace ' + owners[0].name
        : owners.length === 2 ? 'Replace both' : 'Replace all ' + owners.length;

    return (
        <div className="OwnerOpen-block">
            <div className="OwnerOpen-label">This property already has an owner</div>
            <div className="OwnerSegment">
                <button
                    type="button"
                    className={'OwnerSegment-option' + (alongside ? '' : ' OwnerSegment-option--on')}
                    onClick={() => onChange(false)}
                >
                    {replace}
                </button>
                <button
                    type="button"
                    className={'OwnerSegment-option' + (alongside ? ' OwnerSegment-option--on' : '')}
                    onClick={() => onChange(true)}
                >
                    Add as co-owner
                </button>
            </div>
        </div>
    );
}

// Attach the record on file, or file the person new beside it: a match on one
// shared email under another name is exactly what a person has to decide.
// When the record is the person, both names are the same, so neither is repeated.
function RecordChoice({ record, person, same, asNew, onChange }) {
    return (
        <div className="OwnerOpen-block">
            <div className="OwnerOpen-label">Record on file</div>
            <div className="OwnerSegment OwnerSegment--stacked">
                <button
                    type="button"
                    className={'OwnerSegment-option' + (asNew ? '' : ' OwnerSegment-option--on')}
                    onClick={() => onChange(false)}
                >
                    {same ? 'Use this investor' : 'Use ' + record}
                </button>
                <button
                    type="button"
                    className={'OwnerSegment-option' + (asNew ? ' OwnerSegment-option--on' : '')}
                    onClick={() => onChange(true)}
                >
                    {same ? 'Add as new investor' : 'File ' + person + ' as a new investor'}
                </button>
            </div>
        </div>
    );
}

function Fact({ label, value }) {
    if (!value) return null;

    return (
        <div className="OwnerOpen-fact">
            <span className="OwnerOpen-factLabel">{label}</span>
            <span className="OwnerOpen-factValue">{value}</span>
        </div>
    );
}

/**
 * What picking this row would write over on the investor iCLIMB already holds.
 *
 * The same tick-per-field control the LightBox property diff uses, for the
 * same reason: a value somebody typed is only replaced when they say so. A
 * blank column arrives ticked; a filled one does not.
 */
function Changes({ rows, fields, onToggle }) {
    if (!rows.length) return null;

    return (
        <div className="OwnerOpen-block">
            <div className="OwnerOpen-label">Update the record on file</div>
            {rows.map((row) => {
                const on = fields.indexOf(row.key) !== -1;

                return (
                    <div key={row.key} className="OwnerOpen-change" onClick={() => onToggle(row.key)}>
                        <div className={'Check' + (on ? ' Check--on' : '')}><TickIcon /></div>
                        <div className="OwnerOpen-changeBody">
                            <div className="OwnerOpen-changeLabel">{row.label}</div>
                            <div className="OwnerOpen-changeValues">
                                <span className="OwnerOpen-was">{row.current || '—'}</span>
                                <span className="OwnerOpen-arrow">→</span>
                                <span className="OwnerOpen-now">{row.incoming}</span>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

/** The investors iCLIMB holds that the automatic match did not find. */
function Search({ onPick }) {
    const [term, setTerm] = useState('');
    const [results, setResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        const query = term.trim();

        if (query.length < SEARCH_MIN_CHARS) {
            setResults([]);
            setError('');
            setIsSearching(false);

            return undefined;
        }

        let live = true;

        setIsSearching(true);

        const timer = setTimeout(() => {
            mapApi
                .searchInvestors(query)
                .then((investors) => {
                    if (!live) return;

                    setResults(investors);
                    setError('');
                    setIsSearching(false);
                })
                .catch((problem) => {
                    if (!live) return;

                    setResults([]);
                    setError(problem.message);
                    setIsSearching(false);
                });
        }, SEARCH_DEBOUNCE_MS);

        return () => {
            live = false;
            clearTimeout(timer);
        };
    }, [term]);

    return (
        <div className="OwnerSearch">
            {/* No type attribute, like every other input in this panel:
                _base.scss styles input[type="text"] and would beat the card. */}
            <input
                className="OwnerSearch-input"
                placeholder="Search investors by name, company or address"
                value={term}
                onChange={(event) => setTerm(event.target.value)}
            />
            {isSearching && <div className="OwnerSearch-note">Searching…</div>}
            {!isSearching && error && <div className="OwnerSearch-note OwnerSearch-note--error">{error}</div>}
            {!isSearching && !error && term.trim().length >= SEARCH_MIN_CHARS && results.length === 0 && (
                <div className="OwnerSearch-note">No investor matches that.</div>
            )}
            {results.map((investor) => (
                <button
                    type="button"
                    key={investor.ARCIMEDES_ROW_ID}
                    className="OwnerSearch-row"
                    onClick={() => {
                        onPick({
                            id: String(investor.ARCIMEDES_ROW_ID),
                            name: investor.NAME,
                            company: investor.FIRM_NAME || null,
                            reason: [investor.ADDRESS, investor.CITY, investor.STATE].filter(Boolean).join(' · ') || 'Found by search',
                        });
                        setTerm('');
                    }}
                >
                    <span className="OwnerSearch-name">{investor.NAME}</span>
                    {investor.DECEASED && <span className="OwnerPill OwnerPill--deceased">Deceased</span>}
                    {investor.FIRM_NAME && <span className="OwnerSearch-sub"> · {investor.FIRM_NAME}</span>}
                    {investor.SECOND_CONTACT_NAME && (
                        <span className="OwnerSearch-sub"> · 2nd Contact: {investor.SECOND_CONTACT_NAME}</span>
                    )}
                </button>
            ))}
        </div>
    );
}

export default function OwnerList({
    parcel,
    owner,
    onChange,
    candidates,
    found,
    onFound,
    owners,
    reloadToken,
    onSave,
    isSaving,
    isCheckingKore,
}) {
    const [rows, setRows] = useState([]);
    const [hasRecord, setHasRecord] = useState(true);
    // The block the save files on the property and the lead, composed by the
    // server so the card copies exactly that rather than a second rendering of
    // the same data.
    const [note, setNote] = useState('');
    const [copied, setCopied] = useState(false);
    const tickTimer = useRef(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    const parcelId = parcel.parcel_id;
    const ownerName = parcel.owner_name;

    const load = useCallback(
        (refresh) => {
            if (!parcelId) {
                setIsLoading(false);

                return undefined;
            }

            let live = true;

            setIsLoading(true);
            setError('');

            mapApi
                .trueOwners(parcelId, ownerName, refresh)
                .then((envelope) => {
                    if (!live) return;

                    setRows(envelope.data || []);
                    setHasRecord(Boolean(envelope.meta && envelope.meta.has_record));
                    setNote((envelope.meta && envelope.meta.note) || '');
                    setIsLoading(false);
                })
                .catch((problem) => {
                    if (!live) return;

                    setRows([]);
                    setNote('');
                    setError(problem.message);
                    setIsLoading(false);
                });

            return () => {
                live = false;
            };
        },
        [parcelId, ownerName]
    );

    // reloadToken changes after a save, so the list re-reads who iCLIMB holds
    // and the person just attached comes back marked as this property's owner.
    useEffect(() => load(false), [load, reloadToken]);

    // Cleared on unmount: the tick below outlives a card closed right after the
    // copy, and React 16 warns about a state update on a gone component.
    useEffect(() => () => window.clearTimeout(tickTimer.current), []);

    // A tick for a moment, then back to the copy mark. The clipboard API needs
    // a secure context and a permission the browser can refuse, so the fallback
    // is a hidden textarea and execCommand — the app still runs over plain http
    // locally, where the modern call is simply absent.
    const copyNote = () => {
        const done = () => {
            setCopied(true);
            window.clearTimeout(tickTimer.current);
            tickTimer.current = window.setTimeout(() => setCopied(false), 1600);
        };

        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(note).then(done, () => fallbackCopy(note) && done());

            return;
        }

        if (fallbackCopy(note)) done();
    };

    const ownerIds = (owners || []).map((each) => String(each.id));

    // Every record on file for a LightBox person is offered in the iCLIMB list, first
    // as the client asked, saying whom it is for: iCLIMB can hold one person twice and
    // only a person can pick the right one. One that already owns this property is not re-offered.
    const held = [];
    const onFileFor = {};

    rows.forEach((row) => onFileOf(row).forEach((record) => {
        const id = String(record.id);

        if (ownerIds.indexOf(id) !== -1 || onFileFor[id]) return;
        onFileFor[id] = rowName(row);
        held.push(record);
    }));

    const listed = (candidates || []).slice();

    (found || []).concat(held.map((record) => ({
        id: String(record.id),
        name: record.name,
        company: record.company || null,
    }))).forEach((investor) => {
        if (!listed.some((candidate) => String(candidate.id) === String(investor.id))) listed.push(investor);
    });

    // The assessor's entity is often a LightBox row of its own — a company with
    // nobody named behind it. Two rows for one company is exactly the
    // duplication this card exists to remove, so the badge moves onto the row
    // that also carries its address, its phones and its firm.
    const recordRow = rows.filter((row) => !row.name && row.matches_assessor)[0] || null;

    // Under the whole list rather than inside one row. It sat in the body of
    // the owner-of-record row, which stays collapsed until it is clicked, and
    // the client's review asked "where you able to create a Comptroller link?
    // if so where is it?" — it had been there all along and unreachable.
    const comptroller = parcel.owner_name && String(parcel.state || '').toUpperCase() === 'TX'
        ? <div className="OwnerList-comptroller"><ComptrollerLink entityName={parcel.owner_name} ownerAddress={parcel.owner_address} /></div>
        : null;

    const pickTrueOwner = (row) => {
        // The gaps arrive ticked and a value somebody typed does not — that
        // rule is the server's, so the ticks start from what it sent.
        const changes = (row.existing_investor && row.existing_investor.changes) || [];

        onChange({
            kind: 'true',
            key: row.key,
            // An owner iCLIMB already holds is attached, not filed, until the
            // row says to file them new (`asNew`): then Kore is asked about
            // them first, like anyone else. The name is for that question.
            existing: Boolean(row.existing_investor),
            // The record's own lead is not a Kore match for the person filed new beside it.
            heldLeadId: row.existing_investor ? row.existing_investor.lead_id || null : null,
            asNew: false,
            name: row.name || (row.business && row.business.name) || null,
            company: row.name && row.business ? row.business.name : null,
            contact: { phone: row.default_phone, cell: row.default_cell, email: row.default_email },
            fields: changes.filter((change) => change.fills_gap).map((change) => change.key),
        });
    };

    const setContact = (part, value) => onChange({ ...owner, contact: { ...owner.contact, [part]: value } });

    // The decision ends where it was made. In the footer it sat above the two
    // actions this card already had and read as a third, unrelated one.
    const commit = onSave ? (
        <React.Fragment>
            <OwnershipChoice
                owners={owners || []}
                alongside={Boolean(owner.alongside)}
                onChange={(alongside) => onChange({ ...owner, alongside })}
            />
            <div className="OwnerOpen-commit">
                <button type="button" className="Btn Btn--sm Btn--primary" onClick={onSave} disabled={isSaving || isCheckingKore}>
                    {isCheckingKore ? 'Checking Kore…' : isSaving ? 'Saving…' : 'Save owner'}
                </button>
            </div>
        </React.Fragment>
    ) : null;

    const toggleField = (key) =>
        onChange({
            ...owner,
            fields: owner.fields.indexOf(key) === -1
                ? owner.fields.concat(key)
                : owner.fields.filter((each) => each !== key),
        });

    return (
        <div className="OwnerList">
            {/* First, as the client asked, so an investor already researched is
                attached before LightBox's copy of them becomes a duplicate. */}
            <Group label="iCLIMB">
                <Search
                    onPick={(investor) => {
                        onFound((found || []).some((each) => each.id === investor.id) ? found : (found || []).concat(investor));
                        onChange({ kind: 'investor', id: investor.id, contact: {}, fields: [] });
                    }}
                />
                {listed.length === 0 && (
                    <div className="OwnerList-note">No investor here matches this owner.</div>
                )}
                {listed.map((candidate) => (
                    <Row
                        key={candidate.id}
                        name={candidate.name}
                        sub={[
                            candidate.company,
                            candidate.reason,
                            onFileFor[String(candidate.id)] && 'On file for ' + onFileFor[String(candidate.id)],
                        ].filter(Boolean).join(' · ')}
                        isOn={owner.kind === 'investor' && String(owner.id) === String(candidate.id)}
                        isOpen={owner.touched}
                        onPick={() => onChange({ kind: 'investor', id: candidate.id, contact: {}, fields: [] })}
                    >
                        {commit}
                    </Row>
                ))}
            </Group>

            {/* One card for everything LightBox says about who owns this: the
                people it names, then the entity the tax roll names. Two cards
                both headed "owner of record" was the confusion. */}
            <Group
                label="LightBox"
                action={
                    <React.Fragment>
                        {/* The client pastes this into the CRM or a property's
                            notes themselves — the same text the save files.
                            Worded, not a bare icon: it shipped as one mark
                            beside another and their review asked for a way to
                            copy the owners as though there were none. */}
                        {note !== '' && (
                            <button
                                type="button"
                                className={'OwnerGroup-action OwnerGroup-action--worded' + (copied ? ' OwnerGroup-action--done' : '')}
                                onClick={() => copyNote()}
                                title={copied ? 'Copied' : 'Copy every owner below, as one block'}
                                aria-label="Copy every owner below, as one block"
                            >
                                {copied ? <TickIcon /> : <CopyIcon />}
                                <span>{copied ? 'Copied' : 'Copy all'}</span>
                            </button>
                        )}
                        <button
                            type="button"
                            className="OwnerGroup-action"
                            onClick={() => load(true)}
                            disabled={isLoading}
                            title="Ask LightBox again"
                            aria-label="Ask LightBox again"
                        >
                            <RefreshIcon />
                        </button>
                    </React.Fragment>
                }
            >
                {isLoading && <div className="OwnerList-note">Asking LightBox…</div>}
                {!isLoading && error && <div className="OwnerList-note OwnerList-note--error">{error}</div>}
                {!isLoading && !error && !hasRecord && (
                    <div className="OwnerList-note">LightBox holds no contact record for this parcel.</div>
                )}
                {!isLoading && !error && hasRecord && rows.length === 0 && (
                    <div className="OwnerList-note">LightBox names nobody behind this parcel.</div>
                )}

                {rows.map((row) => {
                    const existing = row.existing_investor;
                    const isOn = owner.kind === 'true' && owner.key === row.key;

                    return (
                        <Row
                            key={row.key}
                            name={rowName(row)}
                            sub={row.name && row.business ? row.business.name : null}
                            pills={
                                <React.Fragment>
                                    {row === recordRow && <span className="OwnerPill OwnerPill--record">Owner of record</span>}
                                    {row.matches_assessor && row !== recordRow && (
                                        <span className="OwnerPill OwnerPill--match">Same entity</span>
                                    )}
                                    {row.most_likely && <span className="OwnerPill OwnerPill--likely">Most likely</span>}
                                    {existing && (
                                        ownerIds.indexOf(existing.id) !== -1
                                            ? <span className="OwnerPill OwnerPill--current">Current owner</span>
                                            : onFileOf(row).some(isThem)
                                                ? <span className="OwnerPill OwnerPill--match">In iCLIMB</span>
                                                : <span className="OwnerPill OwnerPill--onfile">Possible · {existing.name}</span>
                                    )}
                                    {existing && existing.deceased && <span className="OwnerPill OwnerPill--deceased">Deceased</span>}
                                </React.Fragment>
                            }
                            meter={<MatchMeter confidence={row.confidence} />}
                            isOn={isOn}
                            isOpen={owner.touched}
                            onPick={() => pickTrueOwner(row)}
                        >
                            <ContactChoice
                                label="Phone"
                                options={row.phones.filter((phone) => phone.type !== 'C').map((phone) => phone.number)}
                                value={owner.contact.phone}
                                onChange={(value) => setContact('phone', value)}
                            />
                            <ContactChoice
                                label="Mobile"
                                options={row.phones.filter((phone) => phone.type === 'C').map((phone) => phone.number)}
                                value={owner.contact.cell}
                                onChange={(value) => setContact('cell', value)}
                            />
                            <ContactChoice
                                label="Email"
                                options={row.emails}
                                value={owner.contact.email}
                                onChange={(value) => setContact('email', cleanEmail(value))}
                                markOf={(option) => (/\(v\)$/i.test(option) ? 'Verified' : null)}
                                same={(option, chosen) => cleanEmail(option) === cleanEmail(chosen)}
                            />

                            <div className="OwnerOpen-block">
                                <Fact label="Title" value={row.titles.join(', ')} />
                                <Fact label="Address" value={addressLine(row.address)} />
                                {row.business && (
                                    <React.Fragment>
                                        <Fact label="Company" value={row.business.name} />
                                        <Fact label="Company address" value={addressLine(row.business.address)} />
                                        <Fact
                                            label="Company phone"
                                            value={row.business.phones.map((phone) => phone.number).join(', ')}
                                        />
                                        <Fact label="Related" value={row.business.related} />
                                    </React.Fragment>
                                )}
                            </div>

                            {existing && (
                                <React.Fragment>
                                    <div className="OwnerOpen-known">
                                        <span>
                                            {existing.strength === SAME_PERSON
                                                ? existing.name + ' is already on file'
                                                : existing.name + ' is on file and shares a detail with this person'}
                                            {onFileOf(row).length > 1 && ` · ${onFileOf(row).length - 1} more on file, all in the iCLIMB list above`}
                                            {existing.second_contact && ` · 2nd Contact: ${existing.second_contact.name}`}
                                        </span>
                                        <a
                                            className="OwnerOpen-link"
                                            href={'/investor/' + existing.id}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Open <ExternalIcon />
                                        </a>
                                    </div>
                                    <MatchEvidence matched={existing.matched} />
                                    <RecordChoice
                                        record={existing.name}
                                        person={rowName(row)}
                                        same={existing.strength === SAME_PERSON && onFileOf(row).length === 1}
                                        asNew={Boolean(owner.asNew)}
                                        onChange={(asNew) => onChange({ ...owner, asNew })}
                                    />
                                    {!owner.asNew && <Changes rows={existing.changes} fields={owner.fields} onToggle={toggleField} />}
                                </React.Fragment>
                            )}
                            {commit}
                        </Row>
                    );
                })}
                {parcel.owner_name && !recordRow && (
                    <Row
                        name={parcel.owner_name}
                        sub={parcel.owner_address}
                        pills={<span className="OwnerPill OwnerPill--record">Owner of record</span>}
                        isOn={owner.kind === RECORD}
                        isOpen={owner.touched}
                        onPick={() => onChange({ kind: RECORD, contact: {}, fields: [] })}
                    >
                        <div className="OwnerOpen-known">
                            <span>Filed as a company, with no named person and no phone.</span>
                        </div>
                        {commit}
                    </Row>
                )}
                {comptroller}
            </Group>
        </div>
    );
}
